import { getDomain } from 'tldts';

// #769 — does the call logo live on the registrant's own site? The KYC
// "Website & Domain Verification" item is where the analyst answers that, and
// this is the hint beside the logo link on the Onboarding Data card. It is
// informational only: the analyst still decides and records the evidence.

// The URL, if — and only if — it is an absolute http: or https: address. This
// is the gate on rendering registrant-supplied text as an href: anything else
// (javascript:, data:, a relative path, free text) comes back null and is
// shown as plain text instead.
export function httpUrl(raw: unknown): URL | null {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

// The website is often declared without a scheme ("acme.com", "www.acme.com",
// "www.acme.com:8080"), so a bare host is read as https. Anything that already
// names a scheme with "://" goes through httpUrl unchanged, so
// "ftp://acme.com" is unparseable, not acme.com.
function websiteUrl(raw: unknown): URL | null {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return null;
  return httpUrl(/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
}

// "https://acme.com@evil.com" is a URL for evil.com that a person reads as
// acme.com. Nothing legitimate here carries credentials, so a URL that does
// gets no verdict rather than one about a host the analyst is not looking at.
const hasUserinfo = (url: URL) => url.username !== '' || url.password !== '';

// PRIVATE public-suffix entries count: github.io, netlify.app, vercel.app,
// myshopify.com, azurewebsites.net and the like hand a separately-owned
// subdomain to every customer, so evil.github.io and acme.github.io are two
// owners. tldts ignores that section of the list unless asked, and would call
// both "github.io" and report a match.
const PSL_OPTIONS = { allowPrivateDomains: true };

export type LogoDomainMatch =
  | { kind: 'same' }
  | { kind: 'different'; host: string }
  // Either value is missing, or one of them has no registrable domain (free
  // text, an IP address, localhost): nothing to compare, so no verdict.
  | { kind: 'unknown' };

// Compares REGISTRABLE domains (eTLD+1, from the public suffix list,
// private section included — see PSL_OPTIONS), so
// cdn.acme.com matches www.acme.com while acme.co.uk and other.co.uk do not —
// splitting on dots would call both of those "co.uk".
export function compareLogoDomain(logoUrl: unknown, website: unknown): LogoDomainMatch {
  const logo = httpUrl(logoUrl);
  const site = websiteUrl(website);
  if (!logo || !site || hasUserinfo(logo) || hasUserinfo(site)) return { kind: 'unknown' };
  const logoDomain = getDomain(logo.hostname, PSL_OPTIONS);
  const siteDomain = getDomain(site.hostname, PSL_OPTIONS);
  if (!logoDomain || !siteDomain) return { kind: 'unknown' };
  return logoDomain === siteDomain ? { kind: 'same' } : { kind: 'different', host: logo.hostname };
}
